import type { ItemFieldRow } from './item-field-reader';
import { readItemField } from './item-field-reader';
import { compilePictureRow } from './picture-row-construction';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow, FromBasePlayerMetadata } from './from-base-context';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import type { FormulaReadDependencies } from './formula-read-context';
import type { FormulaVariableReaders } from './formula-variable-resolution';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { selectCreaturePicture } from '../../shared/creature-token-geometry';
import { readCreatureStatuses } from './creature-status-reader';
import { statusPaused } from '../statuses/expiry';

/** SELECT media becomes the native definition gallery only at its consumer.
 * Preserve duplicate picture ids: reference copies are distinct database rows. */
export function constructPlayerDefinitionPictures(source: ItemFieldRow, cdnUrl: string | undefined,
    value: (row: ItemFieldRow, key: string) => unknown): FromBaseRow[] {
    const raw = value({ pictures: [(source as FromBaseRow).pictures ?? []] }, 'pictures');
    if (!Array.isArray(raw)) throw new FromBaseInputMissing('raw-definition-pictures', 'gallery');
    return constructPlayerAttachedPictures(raw, 'item', cdnUrl, value);
}

/** Native fetch order and picture compilation over held SELECT rows. */
export function constructPlayerAttachedPictures(raw: readonly FromBaseRow[], type: string, cdnUrl: string | undefined,
    value: (row: ItemFieldRow, key: string) => unknown): FromBaseRow[] {
    const pictures = raw.filter((row): row is FromBaseRow => fromBaseRecord(row)
        && value(row, 'picture_attached_item_type') === type && phpJsonInt(value(row, 'picture_deleted')) === 0);
    if (pictures.length && typeof cdnUrl !== 'string') throw new FromBaseInputMissing('picture-cdn-url', 'gallery');
    const order = ['primary_picture', 'picture_thumbnail', 'picture_number', 'picture_i'];
    pictures.sort((left, right) => {
        for (const [index, key] of order.entries()) {
            const a = value(left, key), b = value(right, key);
            const delta = a == null || b == null ? (a == null ? 0 : 1) - (b == null ? 0 : 1) : phpJsonInt(a) - phpJsonInt(b);
            if (delta) return index < 2 ? -delta : delta;
        }
        return 0;
    });
    return pictures.map(row => compilePictureRow(row, { cdnUrl: cdnUrl! }) as FromBaseRow);
}

/** Native EC presentation pointers can refer to a PC's existing gallery. */
export function constructEncounterSourcePictures(source: FromBaseRow, raw: readonly FromBaseRow[], cdnUrl?: string): FromBaseRow {
    const row = fromBaseClone(source), pictures: FromBaseRow = {};
    const own = constructPlayerAttachedPictures(raw.filter(picture => readItemField(picture, 'picture_attached_id')
        === readItemField(row, 'encounter_creature_id')), 'encounter_creature_picture', cdnUrl, readItemField);
    if (own.length) pictures.encounter_creature_picture = own;
    const presentation = readItemField(row, 'encounter_creature_presentation_pic_id');
    const thumbnail = readItemField(row, 'encounter_creature_thumbnail_pic_id');
    const fallback = fromBaseUuid(presentation) ? presentation : fromBaseUuid(thumbnail) ? thumbnail : readItemField(own[0] ?? null, 'picture_id');
    if (fromBaseUuid(fallback)) {
        row.encounter_creature_presentation_pic_id = fallback;
        row.encounter_creature_thumbnail_pic_id = fromBaseUuid(thumbnail) ? thumbnail : fallback;
    } else {
        row.encounter_creature_presentation_pic_id = presentation;
        row.encounter_creature_thumbnail_pic_id = thumbnail;
    }
    for (const [key, type] of [['encounter_creature_presentation_pic_id', 'encounter_creature_presentation_picture'],
        ['encounter_creature_thumbnail_pic_id', 'encounter_creature_thumbnail_picture']] as const) {
        const id = readItemField(row, key);
        if (!fromBaseUuid(id)) continue;
        const saved = raw.find(picture => readItemField(picture, 'picture_id') === id && phpJsonInt(readItemField(picture, 'picture_deleted')) === 0);
        if (!saved) continue;
        if (!cdnUrl) throw new FromBaseInputMissing('picture-cdn-url', id);
        pictures[type] = [compilePictureRow(saved, { cdnUrl })];
    }
    if (Object.keys(pictures).length) row.pictures = pictures;
    return row;
}

/** Save-section definitions have the same item gallery as build_item_arr. */
export function constructPlayerSaveSectionMedia(source: ItemFieldRow, cdnUrl: string | undefined,
    value: (row: ItemFieldRow, key: string) => unknown): FromBaseRow {
    const gallery = value({ pictures: [(source as FromBaseRow).pictures ?? null] }, 'pictures');
    if (fromBaseRecord(gallery)) return fromBaseClone(source) as FromBaseRow;
    const result = fromBaseClone(source) as FromBaseRow, pictures = constructPlayerDefinitionPictures(source, cdnUrl, value);
    delete result.pictures;
    if (pictures.length) result.pictures = { item: pictures };
    return result;
}

/** get_object_picture_arr's actor path for compile-stable height. Active forms
 * use the same picture selection as their token; ordinary PCs use their own art. */
export function constructPlayerPhysicsPicture(host: FromBaseRow, metadata: FromBasePlayerMetadata,
    deps: FormulaReadDependencies, readers: FormulaVariableReaders, includeItemGallery = false): ItemFieldRow | null {
    const stored = (row: ItemFieldRow | null, key: string): unknown => readers.itemField({ [key]: [row ? (row as FromBaseRow)[key] ?? null : null] }, key);
    if (phpJsonTruthy(readers.itemField(host, 'shapeshifted')) && !metadata.definitionLookup)
        throw new FromBaseStageMissing('size-shapeshift-vitality', 'form-physics-picture');
    if (phpJsonTruthy(readers.itemField(host, 'inventory_id')))
        throw new FromBaseStageMissing('defenses-flags-ac-capacity', 'inventory-physics-picture');
    const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId, resolveCarrierRef: deps.resolveCarrierRef });
    const gallery = (row: ItemFieldRow | null, type: string, visited = new Set<string>()): ItemFieldRow[] => {
        if (!row) return [];
        const id = readNodeItemId(row);
        if (id && visited.has(id)) return [];
        if (id) visited = new Set([...visited, id]);
        const bucket = stored(row, 'pictures');
        if (Array.isArray(bucket)) {
            // Raw C1 definitions carry SELECT rows until their gallery consumer.
            if (type === 'item') return constructPlayerDefinitionPictures(row, metadata.cdnUrl, readers.layerValue);
        } else if (fromBaseRecord(bucket)) {
            const own = stored(bucket, type);
            if (Array.isArray(own) && own.length) return own;
        }
        for (const key of type === 'item' ? ['item_pictures', 'item_picture'] : [type]) {
            const own = stored(row, key);
            if (Array.isArray(own) && own.length) return own;
        }
        const ec = stored(row, 'encounter_creatures') as ItemFieldRow | null;
        if (ec && type === 'encounter_creature_presentation_picture') {
            const pictures = stored(stored(ec, 'pictures') as ItemFieldRow, type);
            if (Array.isArray(pictures) && pictures.length) return pictures;
        }
        for (const provider of providers.providers(row)) {
            const pictures = gallery(provider, type, visited);
            if (pictures.length) return pictures;
        }
        return [];
    };
    if (phpJsonTruthy(readers.itemField(host, 'shapeshifted'))) return selectCreaturePicture(host,
        { use_picture_thumbnail: false }, {
            pictures: (row, key) => gallery(row as ItemFieldRow, key),
            itemPictures: row => gallery(row as ItemFieldRow, 'item'),
            items: (row, key) => readers.itemField(row as ItemFieldRow, key),
            statuses: (row, mode) => Object.values(readCreatureStatuses(row as ItemFieldRow, mode, deps.resolveCarrierRef)) as FromBaseRow[],
            paused: statusPaused,
            picture: (row, key) => readers.itemField(row as ItemFieldRow,
                key === 'pic_url' ? 'picture_url' : key === 'pic_thumbnail' ? 'picture_thumbnail' : key),
        });
    const quick = readers.itemField(host, 'pic_url');
    if (typeof quick === 'string' && quick !== '') return { picture_url: [quick] };
    const pc = stored(host, 'player_characters') as ItemFieldRow | null;
    const ec = stored(host, 'encounter_creatures') as ItemFieldRow | null;
    const inventoryPictures = gallery(host, 'inventory_picture');
    if (inventoryPictures.length) return inventoryPictures[0]!;
    const active = phpJsonTruthy(readers.itemField(ec, 'encounter_creature_id'));
    const pointer = readers.itemField(ec, 'encounter_creature_presentation_pic_id');
    if (active && fromBaseUuid(pointer)) {
        for (const [owner, types] of [[host, ['encounter_creature_presentation_picture', 'encounter_creature_picture', 'item']],
            [pc, ['player_character_picture']]] as const) for (const type of types) {
            const found = gallery(owner, type).find(picture => phpJsonString(readers.itemField(picture, 'picture_id')).toLowerCase() === pointer.toLowerCase());
            if (found) return found;
        }
    }
    if (active) for (const [owner, type] of [[host, 'encounter_creature_picture'], [pc, 'player_character_picture']] as const) {
        const pictures = gallery(owner, type);
        if (!pictures.length) continue;
        // An omitted thumbnail flag is a full image. An empty legacy wrapper
        // defaults to thumbnail, matching the native first-full-size walk.
        return pictures.find(picture => {
            const raw = stored(picture, 'picture_thumbnail');
            return phpJsonInt(Array.isArray(raw) ? raw[0] ?? 1 : raw) === 0;
        }) ?? pictures[0]!;
    }
    return includeItemGallery && !phpJsonTruthy(pc) ? gallery(host, 'item')[0] ?? null : null;
}
